import React from "react";
import { onDiscussionAddedToGroup, onDiscussionRemovedFromGroup } from "@/main/common/EventRegistry";

/**
 * QC-14 / A1 (QoL work order slice `1j`): a per-group counter of group-post mutations, carried in the
 * group post list's `key` and `restorationKey` so a Back-restoration can never re-seed pages that
 * predate a post being added to or removed from the group.
 *
 * <p><b>Why module-level, not component state.</b> The restoration cache (`NavigationListStateCache`)
 * is module memory keyed by `(navigationId, restorationKey)`, and it keeps the pre-mutation entry after
 * the key moves on. A counter in React state would reset to its initial value when the group page
 * remounts on Back, and so would read that stale entry again. This counter lives exactly as long as the
 * cache does (the tab's JS context; a hard reload clears both), so a remount reads the key the list last
 * had. It also counts mutations made while the group page is AWAY (a post removed from the group on the
 * post's own page), which is the Back case the exclusion was protecting against.
 *
 * <p>The sibling is `react/list/viewerMutationKey.ts` (FI-631: the profile and dashboard follow and
 * group-membership lists, which had the page-state shape this replaced). Not a
 * `useSyncExternalStore` store: one integer per group, read at mount and pushed to the one mounted
 * list through a plain subscription, so no chrome subscribes to it (rule 31 does not come into play).
 */
const counters = new Map<string, number>();
const listeners = new Set<(groupPublicId: string) => void>();
let subscribed = false;

function bump(groupPublicId: string | undefined | null) {
    if (!groupPublicId) {
        return;
    }
    counters.set(groupPublicId, (counters.get(groupPublicId) ?? 0) + 1);
    listeners.forEach((listener) => listener(groupPublicId));
}

/** Idempotent: the first group list to load starts counting, and it never stops for the tab's life. */
export function ensureGroupDiscussionsMutationTracking(): void {
    if (subscribed) {
        return;
    }
    subscribed = true;
    onDiscussionAddedToGroup((payload) => bump(payload?.groupPublicId));
    onDiscussionRemovedFromGroup((payload) => bump(payload?.groupPublicId));
}

export function readGroupDiscussionsMutationKey(groupPublicId: string | undefined | null): number {
    return groupPublicId ? (counters.get(groupPublicId) ?? 0) : 0;
}

/** The current counter for `groupPublicId`, re-rendering the caller when this group's posts change. */
export function useGroupDiscussionsMutationKey(groupPublicId: string | undefined | null): number {
    ensureGroupDiscussionsMutationTracking();
    const [value, setValue] = React.useState(() => readGroupDiscussionsMutationKey(groupPublicId));

    React.useEffect(() => {
        // Re-read at subscribe time: a mutation between render and effect must not be missed.
        setValue(readGroupDiscussionsMutationKey(groupPublicId));
        if (!groupPublicId) {
            return;
        }
        const listener = (changed: string) => {
            if (changed === groupPublicId) {
                setValue(readGroupDiscussionsMutationKey(groupPublicId));
            }
        };
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    }, [groupPublicId]);

    return value;
}
